import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { settingsConfig } from 'src/assets/SettingsConfig'
import { SettingConfig } from 'src/lib/types/settings'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR from 'swr'
import ShopSettings from 'src/components/settings/ShopSettings'
import Integrations from 'src/components/settings/Integrations'
import PaymentsSettings from 'src/components/settings/payments/PaymentsSettings'
import MembersRoles from 'src/components/settings/members-roles/MembersRoles'
import OrganizationDetailsForm from 'src/components/settings/billing-usage/OrganizationDetailsForm'
import Channels from 'src/components/settings/channels/Channels'
import BillingUsage from 'src/components/settings/billing-usage/BillingUsage'
import MyProfile from 'src/components/settings/MyProfile'
import { apiURI } from 'src/lib/utils/constants'
import ActivityLogs from '../activity-logs/ActivityLogs'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { mapSettingsPageKeyToQueryParam } from 'src/lib/utils/settings'

const Setting = () => {
  const router = useRouter()
  const { setting_id, s_id } = router.query
  const { asPath } = router
  const [pageData, setPageData] = useState<SettingConfig>()
  const {
    getters: { merchant }
  } = useMerchant()
  const ISSUER_ID = process?.env?.NEXT_PUBLIC_APP_ISSUER_ID
  const [rewardsResponse, setRewardsResponse] = useState<any>(null)
  const [isRewardsValidating, setIsRewardsValidating] = useState(false)

  const { data: settingResponse, isValidating: isSettingDataValidating } = useSWR(
    Boolean(mapSettingsPageKeyToQueryParam(setting_id?.[0]))
      ? [
          `/merchant-attributes/settings?${mapSettingsPageKeyToQueryParam(setting_id?.[0])}=true`,
          apiURI.FLO_KRATOS_CHECKOUT
        ]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const { data: ETDProviders, isValidating: isETDProvidersValidating } = useSWR(
    [`/utility/external-etd-config-providers`, apiURI.SHIPPING_ENGINE],
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  useEffect(() => {
    const fetchRewards = async () => {
      if (!merchant?.accountId || !ISSUER_ID) return

      setIsRewardsValidating(true)
      try {
        const response = await fetcher(
          `/issuer/${ISSUER_ID}/merchant/${merchant.accountId}/configuration`,
          apiURI.REWARDS_ENGINE
        )
        setRewardsResponse(response)
      } catch (error) {
        console.error('Error fetching rewards:', error)
      } finally {
        setIsRewardsValidating(false)
      }
    }

    fetchRewards()
  }, [merchant?.accountId, ISSUER_ID])

  useEffect(() => {
    if (!Boolean(setting_id?.[0])) return
    //Fetch the metadata of the current settings page
    const currentSettingPageData = settingsConfig.find(setting => setting.type === setting_id?.[0])
    if (!currentSettingPageData) return
    setPageData(currentSettingPageData)
  }, [setting_id])

  const renderSettingSection = () => {
    switch (setting_id?.[0]) {
      case 'shop-details': {
        return (
          <div className='w-full lg:w-3/5'>
            <ShopSettings shopData={(settingResponse as any)?.shop_details} />
          </div>
        )
      }
      case 'integrations': {
        return (
          <div className='w-full lg:w-3/5'>
            <Integrations
              apps={(settingResponse as any)?.integration_details?.data?.apps}
              shippingApps={(Array.isArray(ETDProviders) && ETDProviders) || []}
              rewardsApps={merchant?.accountId && rewardsResponse}
            />
          </div>
        )
      }
      case 'payments': {
        return (
          <div className='w-full lg:w-3/5'>
            <PaymentsSettings />
          </div>
        )
      }
      case 'members': {
        return (
          <div className='w-full'>
            <MembersRoles />
          </div>
        )
      }
      case 'billing-usage': {
        return (
          <div className='w-full lg:w-3/5'>
            <BillingUsage />
          </div>
        )
      }
      case 'channels': {
        return (
          <div className='w-full lg:w-3/5'>
            <Channels />
          </div>
        )
      }
      case 'organisational-details': {
        return (
          <div className='w-full lg:w-3/5'>
            <OrganizationDetailsForm isConfigured={false} />
          </div>
        )
      }
      case 'profile': {
        return (
          <div className='w-full lg:w-3/5'>
            <MyProfile />
          </div>
        )
      }
      case 'activity-logs': {
        return (
          <div className='w-full lg:w-3/5'>
            <ActivityLogs />
          </div>
        )
      }
      default:
        return <></>
    }
  }

  const handleBackButtonClick = () => {
    if (!Boolean(asPath)) return
    if (Boolean(s_id)) {
      const queryStartIndex = asPath.indexOf('?')
      const previousPath = asPath.substring(0, queryStartIndex)
      router.push(previousPath)
      return
    }
    const lastPathIndex = asPath.lastIndexOf('/')
    const previousPath = asPath.substring(0, lastPathIndex)
    router.push(previousPath)
  }

  const getPageHeader = () => {
    if (!Boolean(pageData)) return ''

    if (!Boolean(s_id) && !Boolean(setting_id?.[1])) return pageData?.pageHeader
    //For the pages that have a single child only
    if (pageData?.children?.[0]?.type === 'singleton') {
      return pageData?.children?.[0]?.label
    }

    //If no query param is present, no child page is rendered. Hence return the parent page's header

    const childPage = pageData?.children?.find(child => child.type === s_id || child.type === setting_id?.[1])
    if (childPage) return childPage.label
  }

  if (!Boolean(pageData)) return <></>
  return (
    <div className='p-8'>
      <header className='flex flex-row items-center gap-2'>
        <button
          className='px-3 py-2 bg-white border rounded-lg max-w-fit border-flo-gray-light'
          onClick={handleBackButtonClick}
        >
          <ArrowLeft size='16' className='font-medium text-coal-dark ' />
        </button>
        <h1 className='text-xl font-semibold text-carbon'>{getPageHeader()}</h1>
      </header>
      {(setting_id?.[0] === 'profile' ||
        setting_id?.[0] === 'organisational-details' ||
        !Boolean(
          (isETDProvidersValidating && !ETDProviders) ||
            (isSettingDataValidating && !settingResponse) ||
            (isRewardsValidating && !rewardsResponse)
        )) && (
        <>
          <section className='flex flex-row w-full gap-6 mt-5'>
            {renderSettingSection()}
            {/* <div className='w-2/5'>
              <ProgressCard />
            </div> */}
          </section>
        </>
      )}
    </div>
  )
}

export default Setting
